'use client';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type RefObject,
} from 'react';
import dynamic from 'next/dynamic';
import { useModal } from 'components/shared/Modal';
import { useWindowSize } from 'hooks/dom/useWindowSize';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import styles from './RingProVideoModal.module.css';

const DynamicVideoJS = dynamic(() => import('components/heroes/VideoJS'), {
  ssr: false,
});

const VIDEO_FULL = {
  web: 'web_v2/ring-pro/landing-page/videos/ring-pro-video-full-desktop.mp4/w_1920',
  mobile:
    'web_v2/ring-pro/landing-page/videos/ring-pro-video-full-mobile.mp4/w_1080',
};

type ResponsiveSource = string | { mobile: string; web: string };

/* ── Video modal ────────────────────────────────────────── */

const VideoModalPlayer: React.FC<{
  videoSrc: string;
  posterSrc?: string;
  onMount?: () => void;
  onUnmount?: () => void;
}> = ({ videoSrc, posterSrc, onMount, onUnmount }) => {
  useEffect(() => {
    if (onMount) onMount();
    return () => {
      if (onUnmount) onUnmount();
    };
  }, [onMount, onUnmount]);

  const videoOptions = useMemo(() => {
    if (!videoSrc) return undefined as any;
    return {
      autoplay: true,
      controls: true,
      responsive: true,
      fluid: true,
      sources: [{ src: videoSrc, type: 'video/mp4' }],
      playsinline: true,
      preload: 'auto',
      muted: false,
      loop: false,
      poster: posterSrc,
      html5: {
        vhs: { overrideNative: true },
        nativeAudioTracks: false,
        nativeVideoTracks: false,
      },
    } as const;
  }, [videoSrc, posterSrc]);

  if (!videoOptions) return null;

  return (
    <div className={styles.videoModalContent}>
      <DynamicVideoJS key={videoSrc} options={videoOptions} />
    </div>
  );
};

/* ── Hook ────────────────────────────────────────────────── */

export type UseRingProVideoModalOptions = {
  bannerVideoRef?: RefObject<HTMLVideoElement | null>;
};

export function useRingProVideoModal(
  options?: UseRingProVideoModalOptions,
): { openVideoModal: () => void } {
  const modalContext = useModal();
  const { width } = useWindowSize();
  const fallbackRef = useRef<HTMLVideoElement | null>(null);
  const bannerVideoRef = options?.bannerVideoRef ?? fallbackRef;
  const isModalOpenRef = useRef(false);
  const bodyScrollPositionRef = useRef(0);
  const originalHideRef = useRef<(() => void) | null>(null);

  const handleModalOpen = useCallback(() => {
    if (isModalOpenRef.current) return;
    isModalOpenRef.current = true;

    if (bannerVideoRef.current && !bannerVideoRef.current.paused) {
      bannerVideoRef.current.pause();
    }

    bodyScrollPositionRef.current =
      window.pageYOffset || document.documentElement.scrollTop;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${bodyScrollPositionRef.current}px`;
    document.body.style.width = '100%';
  }, [bannerVideoRef]);

  const handleModalClose = useCallback(() => {
    if (!isModalOpenRef.current) return;
    isModalOpenRef.current = false;

    const scrollY = bodyScrollPositionRef.current;
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, scrollY);

    if (bannerVideoRef.current && bannerVideoRef.current.paused) {
      const rect = bannerVideoRef.current.getBoundingClientRect();
      const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;
      if (isInViewport) {
        const playPromise = bannerVideoRef.current.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {
            // Empty Function
          });
        }
      }
    }
  }, [bannerVideoRef]);

  useEffect(() => {
    if (!modalContext) return;
    originalHideRef.current = modalContext.hide;
    const wrappedHide = () => {
      handleModalClose();
      if (originalHideRef.current) originalHideRef.current();
    };
    (modalContext as any).hide = wrappedHide;
    return () => {
      if (originalHideRef.current) {
        (modalContext as any).hide = originalHideRef.current;
      }
    };
  }, [modalContext, handleModalClose]);

  const selectSrc = useCallback(
    (input: ResponsiveSource): string => {
      if (typeof input === 'string') return input;
      if (!width) return input.web;
      return width < 768 ? input.mobile : input.web;
    },
    [width],
  );

  const resolveSrc = useCallback((input: string): string => {
    if (/^(https?:)?\/\//.test(input)) return input;
    const normalized = input.startsWith('/') ? input : `/${input}`;
    return getCompressedAssetUrl(normalized);
  }, []);

  const resolvedVideoSrc = useMemo(() => {
    const src = selectSrc(VIDEO_FULL);
    return resolveSrc(src);
  }, [selectSrc, resolveSrc]);

  const openVideoModal = useCallback(() => {
    handleModalOpen();

    const content = (
      <VideoModalPlayer
        videoSrc={resolvedVideoSrc}
        onMount={handleModalOpen}
        onUnmount={handleModalClose}
      />
    );

    modalContext.set(content);
    modalContext.setPortal(true);
    modalContext.show();
  }, [
    resolvedVideoSrc,
    modalContext,
    handleModalOpen,
    handleModalClose,
  ]);

  return { openVideoModal };
}
